import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { ComponentStore } from '@ngrx/component-store';
import { tapResponse } from '@ngrx/operators';
import { BehaviorSubject, Observable, switchMap } from 'rxjs';
import { ELoadStatus } from '../../../state/state.interface';
import {
  HttpUtilitiesService,
  IGenericCrudRequestConstructionParameters,
  IGetManyResponse,
} from '../../../utilities/http-utilities.service';
import { IDropdownOption } from '../mat-dropdown/mat-dropdown.component';

export interface IFilterCardState {
  boards: IGenericIdName<number, string>[];
  boardsLoading: ELoadStatus;
  dashboards: IGenericIdName<number, string>[];
  dashboardsLoading: ELoadStatus;
  forms: IGenericIdName<number, string>[];
  formsLoading: ELoadStatus;
  itemCategories: IGenericIdName<number, string>[];
  itemCategoriesLoading: ELoadStatus;
  users: IGenericIdName<number, string>[];
  usersLoading: ELoadStatus;
  workflowSteps: IGenericIdName<number, string>[];
  workflowStepsLoading: ELoadStatus;
}

export interface IGenericIdName<idType, nameType> {
  id: idType;
  name: nameType;
}

@Injectable()
export class FilterCardStore extends ComponentStore<IFilterCardState> {
  public static readonly submitSubject: BehaviorSubject<undefined> = new BehaviorSubject<undefined>(undefined);
  public readonly forms: Observable<IDropdownOption[]> = this.select((state: IFilterCardState) =>
    state.forms.map((item) => ({
      id: item.id,
      name: item.name,
    })),
  );
  public readonly dashboards: Observable<IDropdownOption[]> = this.select((state: IFilterCardState) =>
    state.dashboards.map((item) => ({
      id: item.id,
      name: item.name,
    })),
  );

  readonly getFormData = this.effect((trigger$: Observable<IGenericCrudRequestConstructionParameters>) =>
    trigger$.pipe(
      switchMap((params: IGenericCrudRequestConstructionParameters) => {
        this.patchState({ formsLoading: ELoadStatus.loading });
        const parameters: HttpParams = this.httpUtilities.insertGenericCrudRequestParameters(params);

        return this.getForms(parameters).pipe(
          tapResponse(
            (response) =>
              this.patchState({
                forms: response.data,
                formsLoading: ELoadStatus.success,
              }),
            // eslint-disable-next-line no-console
            (error) => console.error('Error loading data', error),
          ),
        );
      }),
    ),
  );

  readonly getDashboardData = this.effect((trigger$: Observable<IGenericCrudRequestConstructionParameters>) =>
    trigger$.pipe(
      switchMap((params: IGenericCrudRequestConstructionParameters) => {
        this.patchState({ dashboardsLoading: ELoadStatus.loading });
        const parameters: HttpParams = this.httpUtilities.insertGenericCrudRequestParameters(params);

        return this.getDashboards(parameters).pipe(
          tapResponse(
            (response) =>
              this.patchState({
                dashboards: response.data,
                dashboardsLoading: ELoadStatus.success,
              }),
            // eslint-disable-next-line no-console
            (error) => console.error('Error loading data', error),
          ),
        );
      }),
    ),
  );

  constructor(
    private readonly http: HttpClient,
    private readonly httpUtilities: HttpUtilitiesService,
  ) {
    super({
      boards: [],
      boardsLoading: ELoadStatus.initial,
      dashboards: [],
      dashboardsLoading: ELoadStatus.initial,
      forms: [],
      formsLoading: ELoadStatus.initial,
      itemCategories: [],
      itemCategoriesLoading: ELoadStatus.initial,
      users: [],
      usersLoading: ELoadStatus.initial,
      workflowSteps: [],
      workflowStepsLoading: ELoadStatus.initial,
    });
  }

  private getForms(params: HttpParams): Observable<IGetManyResponse<IDropdownOption>> {
    return this.http.get<IGetManyResponse<IDropdownOption>>('forms', { params });
  }

  private getDashboards(params: HttpParams): Observable<IGetManyResponse<IDropdownOption>> {
    return this.http.get<IGetManyResponse<IDropdownOption>>('dashboards', { params });
  }
}
